import { Flex, Button, Input, Caption, useStyles } from '@archipro-design/aria';
import { MailAddLine, CheckLine } from '@archipro-design/icons';
import { useRef, useState } from 'react';
import { useFetcher } from '@remix-run/react';

import { useForm, Controller } from 'react-hook-form';
import type { FormShape } from './subscriptionForm.types';
import { validator } from './subscriptionForm.types';

import { pxToRem } from '@archipro-design/aria';
import { zodResolver } from '@hookform/resolvers/zod';

import * as S from './SubscriptionForm.style';
import type { SubscriptionAction } from '~/routes/remix-api.user.subscription';

const SubscriptionForm: React.FC = () => {
    const [formVisible, setFormVisible] = useState(false);

    const {
        control,
        formState: { errors },
        trigger,
        getValues,
    } = useForm<FormShape>({
        resolver: zodResolver(validator),
        mode: 'onSubmit',
    });

    const fetcher = useFetcher<SubscriptionAction>();

    const success =
        fetcher.data && 'success' in fetcher.data && fetcher.data.data;
    const submitting = fetcher.state === 'submitting';
    const fetchedError =
        fetcher.data && 'error' in fetcher.data && fetcher.data.message;

    const errorMessage = errors.email?.message ?? fetchedError ?? '';

    const inputRef = useRef<HTMLInputElement>(null);

    const { keyframe } = useStyles();
    const { css } = useStyles({
        showSuccess: !!success,
        formInputReveal: keyframe(S.FormInputReveal),
        formInputConceal: keyframe(S.FormInputConceal),
        validationTrigged: !!errors.email?.message,
    });

    const onClick = async () => {
        if (!formVisible) {
            setFormVisible(true);
        } else {
            const formValid = await trigger();
            if (!formValid) {
                return;
            }

            const { email } = getValues();

            fetcher.submit(
                { email },
                { method: 'post', action: '/remix-api/user/subscription' }
            );
        }
    };

    return (
        <Flex column={true} className={css(S.Wrap)}>
            {formVisible && (
                <Controller<FormShape>
                    control={control}
                    name={'email'}
                    render={({ field }) => (
                        <Input
                            {...field}
                            placeholder="Please enter your email address"
                            successIndicator={{}}
                            input={{
                                type: 'email',
                                autoFocus: true,
                            }}
                            ref={inputRef}
                            variables={{
                                inputPadding: `${pxToRem(12)} 0`,
                            }}
                            onKeyDown={(e) => {
                                const key = e.code;
                                if (key === 'Enter') {
                                    onClick();
                                    inputRef?.current?.blur();
                                }
                            }}
                            className={css(S.TopBar)}
                        />
                    )}
                />
            )}

            {errorMessage && (
                <Caption variant="03" className={css(S.ErrorState)}>
                    {errorMessage}
                </Caption>
            )}
            {!success && (
                <Button
                    size={16}
                    variables={{ buttonPadding: pxToRem(10), iconPadding: 0 }}
                    iconBefore={<MailAddLine />}
                    transparent={true}
                    onClick={() => {
                        onClick();
                        inputRef?.current?.focus();
                    }}
                    disabled={submitting}
                    className={css(S.FormButton)}
                    type={formVisible ? 'submit' : 'button'}
                >
                    SUBSCRIBE FOR UPDATES
                </Button>
            )}
            {success && (
                <Button
                    size={16}
                    variables={{ buttonPadding: pxToRem(10) }}
                    iconBefore={<CheckLine />}
                    onClick={() => onClick()}
                    disabled={true}
                    transparent={true}
                    className={css(S.Subscribed)}
                >
                    SUBSCRIBED
                </Button>
            )}
        </Flex>
    );
};

export default SubscriptionForm;
